/* ==========================================================================
   Casa Nowa — storefront behaviour
   Vanilla custom elements, no dependencies. Loaded with `defer`.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update', predictiveSearch: '/search/suggest' };
  const strings = theme.strings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const desktop = window.matchMedia('(min-width: 990px)');

  /* ---------- Helpers ---------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const announce = (message) => {
    const region = $('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  };
  // A modal <dialog> makes the rest of the page inert: the toast lives inside the open one
  const toastHost = (el) => {
    const host = $('dialog[open]') || document.body;
    if (el.parentElement !== host) host.appendChild(el);
  };
  const toast = (message) => {
    let el = $('.toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
    }
    toastHost(el);
    el.classList.remove('toast--action');
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 3200);
  };

  /* Toast with one action (e.g. undo), dismissed after 9 s */
  const actionToast = (message, label, onAction) => {
    let el = $('.toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
    }
    toastHost(el);
    el.classList.add('toast--action');
    el.textContent = '';
    const text = document.createElement('span');
    text.textContent = message;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'toast__action';
    button.textContent = label;
    button.addEventListener('click', () => { el.classList.remove('is-visible'); onAction(); }, { once: true });
    el.append(text, button);
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.classList.remove('is-visible'); el.classList.remove('toast--action'); }, 9000);
  };

  /* Shopify money format, e.g. "{{amount_with_comma_separator}} lei" */
  const formatMoney = (cents, format = theme.moneyFormat || '{{amount_with_comma_separator}} lei', trimZeros = true) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) || 0;
    const fmt = (amount, precision, thousands, decimal) => {
      const fixed = (amount / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      let out = frac ? withSep + decimal + frac : withSep;
      if (trimZeros && frac && /^0+$/.test(frac)) out = withSep;
      return out;
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    const map = {
      amount: () => fmt(value, 2, ',', '.'),
      amount_no_decimals: () => fmt(value, 0, ',', '.'),
      amount_with_comma_separator: () => fmt(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(value, 0, '.', ','),
      amount_with_space_separator: () => fmt(value, 2, ' ', ','),
      amount_no_decimals_with_space_separator: () => fmt(value, 0, ' ', ''),
      amount_with_apostrophe_separator: () => fmt(value, 2, "'", '.'),
    };
    return format.replace(/\{\{\s*\w+\s*\}\}/, (map[key] || map.amount)());
  };

  /* ---------- Form validation: inline messages in the store's voice, not browser bubbles ---------- */
  const validationMessage = (el) => {
    const v = el.validity;
    const s = strings.validation || {};
    if (v.valueMissing) {
      // Name the field: "Completează câmpul „Număr comandă”." says more than a generic message
      const label = el.id && document.querySelector(`label[for="${el.id}"]`);
      const name = label && label.childNodes[0] ? label.childNodes[0].textContent.trim() : '';
      if (el.type === 'checkbox' && s.check) return s.check;
      if (name && s.requiredNamed && el.type !== 'radio' && el.type !== 'checkbox') return s.requiredNamed.replace('__FIELD__', name);
      return s.required || el.validationMessage;
    }
    if (v.typeMismatch && el.type === 'email') return s.email || el.validationMessage;
    if (v.tooShort) return (s.short || el.validationMessage).replace('__COUNT__', String(el.minLength));
    return el.validationMessage;
  };
  const showFieldError = (el) => {
    const field = el.closest('.field') || el.parentElement;
    const id = `${el.id || el.name}-error`;
    let msg = document.getElementById(id);
    const described = (el.getAttribute('aria-describedby') || '').split(' ').filter((x) => x && x !== id);
    if (el.validity.valid) {
      if (msg) msg.remove();
      el.removeAttribute('aria-invalid');
      if (described.length) el.setAttribute('aria-describedby', described.join(' ')); else el.removeAttribute('aria-describedby');
      if (field) field.classList.remove('field--error');
      return true;
    }
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'field__error';
      msg.id = id;
      field.appendChild(msg);
    }
    msg.textContent = validationMessage(el);
    el.setAttribute('aria-invalid', 'true');
    el.setAttribute('aria-describedby', [...described, id].join(' '));
    if (field) field.classList.add('field--error');
    return false;
  };
  const validateForm = (form) => {
    let firstInvalid = null;
    $$('input, textarea, select', form).forEach((el) => {
      if (!el.willValidate || el.type === 'hidden' || el.closest('[hidden], [aria-hidden="true"]')) return;
      el.dataset.touched = 'true';
      if (!showFieldError(el) && !firstInvalid) firstInvalid = el;
    });
    if (firstInvalid) firstInvalid.focus();
    return !firstInvalid;
  };
  // Capture phase: runs before any other submit handler and stops it when a field is wrong
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-validate]');
    if (!form || validateForm(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
  document.addEventListener('focusout', (event) => {
    const el = event.target;
    if (!el.matches || !el.matches('form[data-validate] input, form[data-validate] textarea')) return;
    if (el.value.trim() || el.dataset.touched) { el.dataset.touched = 'true'; showFieldError(el); }
  });
  document.addEventListener('input', (event) => {
    const el = event.target;
    if (el.matches && el.matches('form[data-validate] [aria-invalid="true"]')) showFieldError(el);
  });

  /* ---------- Drawers (native <dialog>) ---------- */
  const openDialog = (dialog, trigger, { stack = false } = {}) => {
    if (!dialog || dialog.open) return;
    // A stacked dialog (e.g. the legal notice over the cart) keeps the one underneath open
    if (!stack) $$('dialog[open]').forEach((d) => { if (d !== dialog) d.close(); });
    dialog._trigger = trigger || document.activeElement;
    dialog.showModal();
    if (dialog.hasAttribute('autofocus')) {
      // Focus the dialog itself so screen readers announce it and no control looks pre-selected
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });
    }
    dialog.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  };
  const closeDialog = (dialog) => { if (dialog && dialog.open) dialog.close(); };
  // Shared with scripts loaded later (the discount wheel), so their dialogs get the same history and focus handling
  theme.openDialog = openDialog;
  theme.closeDialog = closeDialog;
  theme.announce = announce;

  /* Back button: an open drawer or dialog gets its own history entry, so Back (or the phone's back gesture)
     closes it instead of leaving the page. Closing it any other way removes that entry again. */
  let ignorePop = false;
  const sameDocLink = (link) => link.target !== '_blank' && !link.hasAttribute('download') && link.origin === window.location.origin
    && !(link.pathname === window.location.pathname && link.search === window.location.search && link.hash);
  document.addEventListener('drawer:open', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || dialog._historyEntry) return;
    dialog._historyEntry = true;
    const state = window.history.state;
    const previous = state && state.cnDialog ? document.getElementById(state.cnDialog) : null;
    // Switching from one drawer to another reuses the entry; a dialog stacked on an open one adds its own
    if (previous && !previous.open) window.history.replaceState({ cnDialog: dialog.id }, '');
    else window.history.pushState({ cnDialog: dialog.id }, '');
    dialog.addEventListener('close', () => {
      dialog._historyEntry = false;
      if (dialog._closedByBack) { dialog._closedByBack = false; return; }
      if (window.history.state && window.history.state.cnDialog === dialog.id) { ignorePop = true; window.history.back(); }
    }, { once: true });
  });
  window.addEventListener('popstate', () => {
    if (ignorePop) { ignorePop = false; return; }
    const open = $$('dialog[open]').filter((d) => d._historyEntry);
    const top = open[open.length - 1];
    if (!top) return;
    top._closedByBack = true;
    top.close();
  });
  // A link followed from inside a drawer replaces the drawer's entry, so Back from the next page returns
  // to this page as it was, not to an invisible "drawer open" step
  document.addEventListener('click', (event) => {
    const link = event.target.closest('dialog[open] a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.closest('[data-open-drawer]') || link.hasAttribute('data-open-drawer') || !sameDocLink(link)) return;
    const dialog = link.closest('dialog');
    if (!dialog._historyEntry || !(window.history.state && window.history.state.cnDialog === dialog.id)) return;
    event.preventDefault();
    window.location.replace(link.href);
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-drawer]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDrawer);
      if (dialog) {
        event.preventDefault();
        openDialog(dialog, opener, { stack: opener.hasAttribute('data-stack') });
      }
      return;
    }
    const closer = event.target.closest('[data-close-drawer]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop closes the dialog
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  document.addEventListener('drawer:open', (event) => {
    const dialog = event.target;
    const input = $('input[type="search"]', dialog);
    if (input) window.requestAnimationFrame(() => { input.focus(); input.select(); });
    if (dialog.classList.contains('drawer--bottom')) {
      const body = $('.drawer__body', dialog);
      if (body) window.requestAnimationFrame(() => body.classList.toggle('no-scroll', body.scrollHeight <= body.clientHeight + 1));
    }
  });

  /* ---------- Drawers: swipe to dismiss on touch (1:1 tracking, velocity, rubber band) ---------- */
  const rubberband = (overshoot, dimension, c = 0.55) => (overshoot * dimension * c) / (dimension + c * Math.abs(overshoot));
  const setupSwipe = (dialog) => {
    if (dialog._swipe) return;
    dialog._swipe = true;
    const vertical = dialog.classList.contains('drawer--bottom');
    // Right drawers close to the right, left drawers to the left, bottom sheets downward
    const closeSign = dialog.classList.contains('drawer--left') ? -1 : 1;
    let start = null;
    let locked = null;
    let delta = 0;
    let history = [];
    const size = () => (vertical ? dialog.offsetHeight : dialog.offsetWidth);
    const reset = () => { start = null; locked = null; delta = 0; history = []; };
    const release = () => { dialog.style.transition = ''; dialog.style.transform = ''; };
    dialog.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch' || !dialog.open || start) return;
      // Leave fields and horizontally scrolling tables alone
      if (e.target.closest('input, textarea, select, [data-no-swipe], .size-guide__scroll')) return;
      // Sheets with scrolling content are dragged by their grabber and header only
      if (vertical && !e.target.closest('.drawer__grabber, .drawer__header, .no-scroll')) return;
      start = { x: e.clientX, y: e.clientY, id: e.pointerId };
      history = [{ v: 0, t: e.timeStamp }];
    });
    dialog.addEventListener('pointermove', (e) => {
      if (!start || e.pointerId !== start.id) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (locked === null) {
        if (Math.hypot(dx, dy) < 10) return; // hysteresis before committing to a direction
        locked = vertical ? Math.abs(dy) > Math.abs(dx) * 1.2 : Math.abs(dx) > Math.abs(dy) * 1.2;
        if (!locked) { reset(); return; }
        try { dialog.setPointerCapture(e.pointerId); } catch (err) { /* pointer already gone */ }
        dialog.style.transition = 'none';
      }
      const raw = vertical ? dy : dx;
      // Toward closing: follow the finger 1:1. Toward opening: progressive resistance.
      delta = raw * closeSign >= 0 ? raw : rubberband(raw, size());
      dialog.style.transform = vertical ? `translateY(${delta}px)` : `translateX(${delta}px)`;
      history.push({ v: raw, t: e.timeStamp });
      if (history.length > 5) history.shift();
    });
    const end = () => {
      if (!start || !locked) { reset(); return; }
      const first = history[0];
      const last = history[history.length - 1];
      const velocity = (last.v - first.v) / Math.max(1, last.t - first.t); // px per ms
      const projected = delta + velocity * 180;
      const shouldClose = projected * closeSign > size() * 0.35 || velocity * closeSign > 0.45;
      // Restore the CSS transition and clear the inline offset in the same frame,
      // so the close (or the snap back) starts from where the finger left it
      release();
      if (shouldClose) closeDialog(dialog);
      reset();
    };
    dialog.addEventListener('pointerup', end);
    dialog.addEventListener('pointercancel', () => { release(); reset(); });
  };
  $$('dialog.drawer:not(.drawer--top)').forEach(setupSwipe);

  /* ---------- Header ---------- */
  class CnHeader extends HTMLElement {
    connectedCallback() {
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.dropdowns = $$('[data-dropdown]', this);
      this.dropdowns.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) this.dropdowns.forEach((d) => { if (d !== details) d.open = false; });
          this.dataset.menuOpen = this.dropdowns.some((d) => d.open) ? 'true' : 'false';
        });
        let timer;
        details.addEventListener('mouseenter', () => {
          if (!finePointer.matches) return;
          clearTimeout(timer);
          timer = setTimeout(() => { details.open = true; }, 80);
        });
        details.addEventListener('mouseleave', () => {
          if (!finePointer.matches) return;
          clearTimeout(timer);
          timer = setTimeout(() => { details.open = false; }, 160);
        });
        details.addEventListener('keyup', (e) => {
          if (e.key === 'Escape' && details.open) {
            details.open = false;
            $('summary', details).focus();
          }
        });
        details.addEventListener('focusout', () => {
          window.setTimeout(() => {
            if (!details.contains(document.activeElement)) details.open = false;
          }, 0);
        });
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.dropdowns.forEach((d) => { d.open = false; });
      });
      this.addEventListener('focusin', () => { this.dataset.hidden = 'false'; });
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    onScroll() {
      const y = window.scrollY;
      const scrolled = y > 24;
      if ((this.dataset.scrolled === 'true') !== scrolled) this.dataset.scrolled = String(scrolled);
      // Phones: the header steps out of the way while reading down and returns on any scroll up
      const last = this._lastY || 0;
      this._lastY = y;
      if (desktop.matches || $('dialog[open]') || this.dataset.menuOpen === 'true') { this.dataset.hidden = 'false'; return; }
      if (Math.abs(y - last) < 6) return;
      const hide = y > last && y > 160;
      if ((this.dataset.hidden === 'true') !== hide) this.dataset.hidden = String(hide);
    }
  }
  define('cn-header', CnHeader);

  /* ---------- Announcement bar: rotation with manual controls ---------- */
  $$('[data-rotate]').forEach((bar) => {
    const items = $$('.announcement__item', bar);
    if (items.length < 2) return;
    let index = 0;
    let paused = false;
    let stopped = reducedMotion.matches;
    const show = (next) => {
      items[index].setAttribute('aria-hidden', 'true');
      index = (next + items.length) % items.length;
      items[index].removeAttribute('aria-hidden');
    };
    const prev = $('[data-announcement-prev]', bar);
    const next = $('[data-announcement-next]', bar);
    // Any manual navigation stops the automatic rotation (WCAG 2.2.2)
    if (prev) prev.addEventListener('click', () => { stopped = true; show(index - 1); });
    if (next) next.addEventListener('click', () => { stopped = true; show(index + 1); });
    bar.addEventListener('mouseenter', () => { paused = true; });
    bar.addEventListener('mouseleave', () => { paused = false; });
    bar.addEventListener('focusin', () => { paused = true; });
    bar.addEventListener('focusout', () => { paused = false; });
    window.setInterval(() => {
      if (stopped || paused || document.hidden) return;
      show(index + 1);
    }, Number(bar.dataset.rotate) || 5000);
  });

  /* ---------- Cookie preferences: reopen Shopify's consent banner when it is enabled ---------- */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cookie-preferences]');
    const banner = window.privacyBanner;
    if (!link || !banner || typeof banner.showPreferences !== 'function') return; // otherwise: the cookie policy page
    event.preventDefault();
    banner.showPreferences();
  });

  /* ---------- Localization selects ---------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-auto-submit]');
    if (select && select.form) select.form.submit();
  });

  /* ---------- Cart ---------- */
  const cartSections = () => {
    const ids = ['cart-drawer'];
    const page = $('[data-cart-page]');
    const wrapper = page && page.closest('.shopify-section');
    if (wrapper) ids.push(wrapper.id.replace('shopify-section-', ''));
    return ids;
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      if (id === 'cart-drawer') {
        const next = $('[data-cart-drawer-inner]', doc);
        const current = $('#CartDrawer [data-cart-drawer-inner]');
        if (next && current) current.innerHTML = next.innerHTML;
      } else {
        const wrapper = document.getElementById(`shopify-section-${id}`);
        const fresh = doc.getElementById(`shopify-section-${id}`) || doc.body.firstElementChild;
        if (wrapper && fresh) wrapper.innerHTML = fresh.innerHTML;
      }
    });
  };

  // The badge is aria-hidden; screen readers read the hidden label, so both change together
  const cartCountLabel = (count) => {
    const forms = strings.cartCount || {};
    let category = 'other';
    try { category = new Intl.PluralRules(document.documentElement.lang || 'ro').select(count); } catch (e) { /* default */ }
    const text = forms[category] || forms.other;
    return text ? text.replace(/\d+/, String(count)) : '';
  };
  const updateCartCount = (count) => {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count > 0 ? String(count) : ''; });
    const label = cartCountLabel(count);
    if (label) $$('[data-cart-count-label]').forEach((el) => { el.textContent = label; });
  };

  const refreshCount = async () => {
    try {
      const res = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      updateCartCount(cart.item_count);
      return cart;
    } catch (e) { return null; }
  };
  // A code applied from outside the cart (the discount wheel) re-renders the same sections
  theme.cartSections = cartSections;
  theme.renderCartSections = renderCartSections;

  const openCart = () => {
    const drawer = document.getElementById('CartDrawer');
    if (theme.cartType === 'page' || !drawer) {
      window.location.href = routes.cart;
      return;
    }
    openDialog(drawer);
  };

  const addToCart = async (formData, button) => {
    formData.append('sections', cartSections().join(','));
    formData.append('sections_url', window.location.pathname);
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }
    try {
      const res = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || strings.cartError || 'Error');
      }
      renderCartSections(data.sections);
      await refreshCount();
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { items: data.items || [data] } }));
      announce(strings.added || '');
      if (theme.cartType === 'page') {
        window.location.href = routes.cart;
      } else {
        openCart();
      }
      return data;
    } finally {
      if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); }
    }
  };

  const changeLine = async (key, quantity, item) => {
    if (item) item.classList.add('is-updating');
    try {
      const res = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: cartSections(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
      renderCartSections(data.sections);
      updateCartCount(data.item_count);
      return true;
    } catch (error) {
      if (item) {
        item.classList.remove('is-updating');
        const err = $('[data-line-error]', item);
        if (err) { err.hidden = false; err.textContent = error.message || strings.cartError; }
      }
      return false;
    }
  };

  // Cart icon opens the drawer
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-open-cart]');
    if (!trigger || theme.cartType === 'page' || !document.getElementById('CartDrawer')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    openCart();
  });

  // Quantity steppers, inputs and remove links (drawer + cart page)
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (step) {
      const wrapper = step.closest('quantity-input');
      const input = $('input', wrapper);
      if (step.getAttribute('aria-disabled') === 'true') return;
      const next = Math.max(0, Number(input.value) + Number(step.dataset.qtyStep));
      if (input.max && next > Number(input.max)) return;
      input.value = next;
      changeLine(input.dataset.key, next, step.closest('[data-cart-item]'));
      return;
    }
    const remove = event.target.closest('[data-remove-line]');
    if (remove) {
      event.preventDefault();
      const item = remove.closest('[data-cart-item]');
      const variantId = item && item.dataset.variantId;
      const quantity = item ? Number(item.dataset.quantity) || 1 : 1;
      changeLine(remove.dataset.key, 0, item).then((ok) => {
        if (!ok || !variantId) return;
        actionToast(strings.removed || '', strings.undo || 'Undo', () => {
          const data = new FormData();
          data.append('id', variantId);
          data.append('quantity', String(quantity));
          data.append('sections', cartSections().join(','));
          data.append('sections_url', window.location.pathname);
          fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data })
            .then((r) => r.json().then((res) => {
              if (!r.ok || res.status) throw new Error(res.description || res.message || strings.cartError);
              renderCartSections(res.sections);
              return refreshCount();
            }))
            .catch((error) => toast(error.message || strings.cartError));
        });
      });
    }
  });
  document.addEventListener('change', (event) => {
    const input = event.target.closest('.quantity__input');
    if (!input) return;
    const value = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(input.dataset.key, value, input.closest('[data-cart-item]'));
  });
  const saveNote = debounce((value) => {
    fetch(`${routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: value }),
    }).catch(() => {});
  }, 500);
  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (note) saveNote(note.value);
  });
  define('quantity-input', class extends HTMLElement {});

  // Quick add on product cards
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-quick-add]');
    if (!form) return;
    event.preventDefault();
    const button = event.submitter;
    if (!button || !button.value) return;
    const data = new FormData();
    data.append('id', button.value);
    data.append('quantity', '1');
    try {
      await addToCart(data, button);
    } catch (error) {
      toast(error.message);
    }
  });

  /* ---------- Product form (PDP) ---------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;
    event.preventDefault();
    const button = $('[data-atc]', form);
    const errorBox = $('[data-form-error]', form);
    if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }
    // Sold out (the whole product or the chosen size): nothing to ask for, the button already says so
    if (button && (button.dataset.state === 'soldout' || button.getAttribute('aria-disabled') === 'true')) return;
    const idInput = $('[data-variant-id]', form);
    if (!idInput || !idInput.value || idInput.disabled) {
      const picker = $('variant-picker');
      if (picker) picker.requestSelection();
      return;
    }
    try {
      await addToCart(new FormData(form), button);
    } catch (error) {
      if (errorBox) { errorBox.hidden = false; errorBox.textContent = error.message; }
    }
  });

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      try {
        this.variants = JSON.parse($('[data-variants]', this).textContent);
      } catch (e) { this.variants = []; }
      this.form = document.getElementById(`ProductForm-${this.dataset.section}`);
      this.addEventListener('change', () => this.onChange());
    }
    selectedOptions() {
      return $$('fieldset[data-option-index]', this).map((fs) => {
        const checked = $('input:checked', fs);
        return checked ? checked.value : null;
      });
    }
    onChange() {
      this.classList.remove('is-invalid');
      const hint = $('.pdp__hint', this);
      if (hint) hint.remove();
      const options = this.selectedOptions();
      if (options.some((o) => o === null)) return;
      const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
      this.applyVariant(variant);
    }
    applyVariant(variant) {
      const form = this.form;
      if (!form) return;
      const idInput = $('[data-variant-id]', form);
      const button = $('[data-atc]', form);
      const label = $('[data-atc-label]', form);
      const dynamic = $('[data-dynamic-checkout]', form);
      if (!variant) {
        idInput.disabled = true;
        button.dataset.state = 'unavailable';
        button.setAttribute('aria-disabled', 'true');
        label.textContent = strings.unavailable;
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: null } }));
        return;
      }
      idInput.value = variant.id;
      idInput.disabled = false;
      if (dynamic) dynamic.classList.remove('is-disabled');
      if (variant.available) {
        button.dataset.state = 'ready';
        button.removeAttribute('aria-disabled');
        label.textContent = strings.addToCart;
      } else {
        button.dataset.state = 'soldout';
        button.setAttribute('aria-disabled', 'true');
        label.textContent = strings.soldOut;
        if (dynamic) dynamic.classList.add('is-disabled');
      }
      // price
      const priceEl = $('[data-product-price] [data-price]');
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      const compareEl = $('[data-product-price] [data-compare-price]');
      if (compareEl) {
        compareEl.textContent = variant.compare_at_price && variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
      }
      const sticky = $('[data-sticky-price]');
      if (sticky) sticky.textContent = formatMoney(variant.price);
      // url
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }
    requestSelection() {
      this.classList.add('is-invalid');
      if (!$('.pdp__hint', this)) {
        const hint = document.createElement('p');
        hint.className = 'pdp__hint';
        hint.setAttribute('role', 'alert');
        hint.textContent = strings.chooseSize;
        this.appendChild(hint);
      }
      this.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
      const first = $('input[data-option-value]:not([data-unavailable])', this) || $('input[data-option-value]', this);
      if (first) first.focus({ preventScroll: true });
    }
  }
  define('variant-picker', VariantPicker);

  /* ---------- Product gallery (mobile counter + zoom) ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.counter = $('[data-gallery-current]', this);
      if (this.track && this.counter) {
        // The slide under the track's left edge, read from the scroll position: robust when the layout changes
        // between grid (desktop) and swipe (phone) after load
        const count = $$('.pdp__media-item', this.track).length;
        let frame = 0;
        const update = () => {
          frame = 0;
          const w = this.track.clientWidth || 1;
          const i = Math.min(count, Math.max(1, Math.round(this.track.scrollLeft / w) + 1));
          if (this.counter.textContent !== String(i)) this.counter.textContent = String(i);
        };
        const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
        this.track.addEventListener('scroll', schedule, { passive: true });
        if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(this.track);
        update();
      }
      const zoomDialog = this.closest('[data-section-id]') && document.getElementById(`Zoom-${this.closest('[data-section-id]').dataset.sectionId}`);
      if (zoomDialog && !zoomDialog._tapZoom) {
        zoomDialog._tapZoom = true;
        zoomDialog.addEventListener('click', (event) => {
          const img = event.target.closest('.zoom__item img');
          if (!img) return;
          const item = img.parentElement;
          const box = img.getBoundingClientRect();
          const fx = (event.clientX - box.left) / box.width;
          const fy = (event.clientY - box.top) / box.height;
          const on = !item.classList.contains('is-zoomed');
          if (!img.dataset.sizes) img.dataset.sizes = img.sizes;
          img.sizes = on ? '250vw' : img.dataset.sizes;
          item.classList.toggle('is-zoomed', on);
          if (on) {
            window.requestAnimationFrame(() => {
              item.scrollLeft = fx * item.scrollWidth - item.clientWidth / 2;
              item.scrollTop = fy * item.scrollHeight - item.clientHeight / 2;
            });
          } else {
            item.scrollIntoView({ block: 'nearest' });
          }
        });
        zoomDialog.addEventListener('close', () => $$('.zoom__item.is-zoomed', zoomDialog).forEach((item) => {
          item.classList.remove('is-zoomed');
          const img = $('img', item);
          if (img && img.dataset.sizes) img.sizes = img.dataset.sizes;
        }));
      }
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-zoom-open]');
        if (!btn) return;
        const section = this.closest('[data-section-id]');
        const zoom = section && document.getElementById(`Zoom-${section.dataset.sectionId}`);
        if (!zoom) return;
        openDialog(zoom, btn);
        const target = $(`[data-zoom-item="${btn.dataset.zoomOpen}"]`, zoom);
        if (target) window.requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
      });
    }
  }
  define('product-gallery', ProductGallery);

  /* ---------- Buy bar (mobile): from the first screen, with a size sheet ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      const buy = this.form && this.form.closest('[data-buy-buttons]');
      if (!this.form || !buy) return;
      this.hidden = false;
      this.button = $('[data-sticky-atc]', this);
      this.label = $('[data-sticky-label]', this);
      this.size = $('[data-sticky-size]', this);
      this.sheet = this.dataset.sheet ? document.getElementById(this.dataset.sheet) : null;
      // Visible whenever the main button is out of view, including on the first screen. It steps aside while the
      // "wear it together" block is on screen (that block adds the other fit) and at the footer, so it never covers
      // the payment icons or the ANPC SAL pictogram.
      const covering = new Set();
      let buyInView = false;
      const update = () => this.classList.toggle('is-visible', !buyInView && covering.size === 0 && !desktop.matches);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => { buyInView = entry.isIntersecting; update(); }, { rootMargin: '0px 0px -8% 0px' }).observe(buy);
        const others = [$('[data-pair-form]'), $('.footer')].map((el) => el && (el.closest('.shopify-section') || el)).filter(Boolean);
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { if (entry.isIntersecting) covering.add(entry.target); else covering.delete(entry.target); });
          update();
        }, { rootMargin: '0px 0px -12% 0px' });
        others.forEach((el) => io.observe(el));
      } else {
        update();
      }
      this.button.addEventListener('click', () => this.onClick());
      document.addEventListener('variant:change', (event) => this.sync(event.detail.variant));
      if (this.sheet) this.bindSheet();
    }
    onClick() {
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      const idInput = $('[data-variant-id]', this.form);
      if (!idInput.value || idInput.disabled) {
        if (this.sheet) { openDialog(this.sheet, this.button); return; }
        const picker = $('variant-picker');
        if (picker) picker.requestSelection();
        return;
      }
      this.form.requestSubmit();
    }
    sync(variant) {
      let state = 'unavailable';
      if (variant) state = variant.available ? 'ready' : 'soldout';
      this.button.dataset.state = state;
      if (state === 'ready') this.button.removeAttribute('aria-disabled'); else this.button.setAttribute('aria-disabled', 'true');
      if (this.label) this.label.textContent = state === 'ready' ? strings.addToCart : state === 'soldout' ? strings.soldOut : strings.unavailable;
      if (this.size) this.size.textContent = variant ? (strings.sizeSelected || '__SIZE__').replace('__SIZE__', variant.title) : '';
    }
    bindSheet() {
      $$('[data-sheet-size]', this.sheet).forEach((option) => {
        option.addEventListener('click', async () => {
          const input = $$('input[data-option-value]').find((el) => el.value === option.dataset.sheetSize);
          if (!input) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          option.classList.add('is-loading');
          option.setAttribute('aria-busy', 'true');
          try {
            await addToCart(new FormData(this.form), $('[data-atc]', this.form));
          } catch (error) {
            toast(error.message || strings.cartError);
          } finally {
            option.classList.remove('is-loading');
            option.removeAttribute('aria-busy');
          }
        });
      });
    }
  }
  define('sticky-atc', StickyAtc);

  /* ---------- Size tables: say when a table scrolls sideways, and fade the edge until its end ---------- */
  const watchTableScroll = (box) => {
    if (box._watched) return;
    box._watched = true;
    const hint = box.previousElementSibling && box.previousElementSibling.matches('[data-scroll-hint]') ? box.previousElementSibling : null;
    const update = () => {
      const overflow = box.scrollWidth > box.clientWidth + 2;
      box.dataset.overflow = String(overflow);
      box.dataset.atEnd = String(box.scrollLeft + box.clientWidth >= box.scrollWidth - 2);
      if (hint) hint.hidden = !overflow;
    };
    box.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(box);
    update();
  };
  $$('.size-guide__scroll').forEach(watchTableScroll);
  document.addEventListener('drawer:open', (event) => {
    $$('.size-guide__scroll', event.target).forEach(watchTableScroll);
    // The size you picked is highlighted in every table of the guide
    if (!event.target.classList.contains('size-guide')) return;
    const checked = $('variant-picker input[data-option-value]:checked');
    $$('tr[data-size]', event.target).forEach((row) => row.classList.toggle('is-selected', !!checked && row.dataset.size === checked.value));
  });

  /* ---------- Product page: a tall info column scrolls with the page, then holds at its end ---------- */
  const pdpInfo = $('.pdp__info');
  if (pdpInfo && 'ResizeObserver' in window) {
    const placeInfo = () => {
      if (!desktop.matches) { pdpInfo.style.top = ''; return; }
      const header = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 5) * 16;
      pdpInfo.style.top = `${Math.min(header + 32, window.innerHeight - pdpInfo.offsetHeight - 24)}px`;
    };
    new ResizeObserver(placeInfo).observe(pdpInfo);
    window.addEventListener('resize', debounce(placeInfo, 120));
  }

  /* ---------- Wear it together: the other fit, added from the product page ---------- */
  const pairState = (form, invalid) => {
    const hint = $('[data-pair-hint]', form);
    const sizes = $('.pair__sizes', form);
    if (hint) hint.hidden = !invalid;
    if (sizes) sizes.classList.toggle('is-invalid', invalid);
  };
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-pair-form]');
    if (!form) return;
    event.preventDefault();
    if (!$('input[name="id"]:checked', form)) {
      pairState(form, true);
      const first = $('input[name="id"]:not(:disabled)', form);
      if (first) first.focus();
      return;
    }
    pairState(form, false);
    try {
      await addToCart(new FormData(form), event.submitter || $('[data-pair-submit]', form));
    } catch (error) {
      toast(error.message || strings.cartError);
    }
  });
  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-pair-form]');
    if (form) pairState(form, false);
  });

  /* ---------- Hero slideshow ---------- */
  class CnSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-slide]', this);
      this.dots = $$('[data-slide-to]', this);
      this.current = 0;
      this.interval = Number(this.dataset.interval) || 7000;
      this.style.setProperty('--interval', `${this.interval}ms`);
      if (this.slides.length < 2) return;
      this.dots.forEach((dot) => dot.addEventListener('click', () => { this.goTo(Number(dot.dataset.slideTo)); this.restart(); }));
      this.toggle = $('[data-slideshow-toggle]', this);
      if (this.toggle) this.toggle.addEventListener('click', () => this.setPaused(this.dataset.paused !== 'true', true));
      this.addEventListener('mouseenter', () => { if (!this.userPaused) this.setPaused(true); });
      this.addEventListener('mouseleave', () => { if (!this.userPaused) this.setPaused(false); });
      this.addEventListener('focusin', () => { if (!this.userPaused) this.setPaused(true); });
      document.addEventListener('visibilitychange', () => { if (!this.userPaused) this.setPaused(document.hidden); });
      if (this.dataset.autoplay === 'true' && !reducedMotion.matches) this.start();
      else if (this.toggle) this.toggle.hidden = true;
    }
    start() {
      this.dataset.playing = 'true';
      this.timer = window.setInterval(() => { if (this.dataset.paused !== 'true') this.goTo(this.current + 1); }, this.interval);
    }
    restart() {
      if (this.dataset.playing !== 'true') return;
      window.clearInterval(this.timer);
      this.start();
      this.dots.forEach((d) => { const bar = $('.hero__dot-bar', d); bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = ''; });
    }
    setPaused(paused, byUser = false) {
      this.dataset.paused = String(paused);
      if (byUser) {
        this.userPaused = paused;
        if (this.toggle) this.toggle.setAttribute('aria-pressed', String(paused));
      }
    }
    goTo(index) {
      const next = (index + this.slides.length) % this.slides.length;
      if (next === this.current) return;
      const prev = this.slides[this.current];
      const slide = this.slides[next];
      prev.classList.remove('is-active');
      prev.setAttribute('aria-hidden', 'true');
      prev.inert = true;
      slide.classList.add('is-active');
      slide.removeAttribute('aria-hidden');
      slide.inert = false;
      $$('img[loading="lazy"]', slide).forEach((img) => { img.loading = 'eager'; });
      this.dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === next);
        if (i === next) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
      const counter = $('[data-slide-current]', this);
      if (counter) counter.textContent = String(next + 1).padStart(2, '0');
      this.current = next;
    }
  }
  define('cn-slideshow', CnSlideshow);

  /* ---------- Fit comparison: one figure, two fits, a curved line to drag ---------- */
  class FitCompare extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.compare');
      this.tabs = $$('[role="tab"]', this);
      this.panels = $$('[data-panel]', this);
      this.current = 0;
      this.x = 50;
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.show(Number(tab.dataset.tab)));
        tab.addEventListener('keydown', (event) => this.onTabKey(event, tab));
      });
      this.panels.forEach((panel) => this.bindStage(panel));
      document.addEventListener('capsule:set', (event) => {
        if (event.detail.source === this) return;
        const index = this.panels.findIndex((p) => p.dataset.setKey === event.detail.key);
        if (index > -1) this.show(index, { broadcast: false, animate: false });
      });
      this.hint();
    }
    onTabKey(event, tab) {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const n = this.tabs.length;
      const i = Number(tab.dataset.tab);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? n - 1 : (i + (event.key === 'ArrowRight' ? 1 : -1) + n) % n;
      this.tabs[next].focus();
      this.show(next);
    }
    show(index, { broadcast = true, animate = true } = {}) {
      const panel = this.panels[index];
      if (!panel || index === this.current) return;
      this.current = index;
      this.panels.forEach((p, i) => { p.hidden = i !== index; });
      this.tabs.forEach((t, i) => {
        t.setAttribute('aria-selected', String(i === index));
        if (i === index) t.removeAttribute('tabindex'); else t.setAttribute('tabindex', '-1');
      });
      // The section takes the colors of the set
      if (this.section && panel.dataset.scheme) {
        Array.from(this.section.classList).forEach((c) => { if (c.startsWith('scheme-')) this.section.classList.remove(c); });
        this.section.classList.add(`scheme-${panel.dataset.scheme}`);
      }
      // The line stays where the visitor left it
      const stage = $('[data-stage]', panel);
      if (stage) this.setX(stage, this.x);
      $$('img[loading="lazy"]', panel).forEach((img) => { img.loading = 'eager'; });
      if (animate && !reducedMotion.matches) {
        panel.classList.remove('is-entering');
        void panel.offsetWidth;
        panel.classList.add('is-entering');
        window.setTimeout(() => panel.classList.remove('is-entering'), 700);
      }
      if (broadcast) document.dispatchEvent(new CustomEvent('capsule:set', { detail: { key: panel.dataset.setKey, source: this } }));
    }
    setX(stage, value) {
      // The puller stays whole inside the frame: the limit is half its width, measured once per frame size
      // (cached, so a drag never forces a layout read between two style writes)
      if (!this._edges) {
        this._edges = new WeakMap();
        window.addEventListener('resize', () => { this._edges = new WeakMap(); }, { passive: true });
      }
      let edge = this._edges.get(stage);
      if (edge === undefined) {
        const puller = $('[data-handle]', stage);
        const width = stage.clientWidth || 1;
        edge = Math.min(20, Math.max(3, (((puller && puller.offsetWidth) || 48) / 2 + 2) / width * 100));
        if (stage.clientWidth) this._edges.set(stage, edge);
      }
      const x = Math.min(100 - edge, Math.max(edge, value));
      this.x = x;
      stage.style.setProperty('--x', `${x.toFixed(2)}%`);
      // The fit names fade as their side closes
      stage.style.setProperty('--tag-a', Math.min(1, Math.max(0, (x - 8) / 14)).toFixed(2));
      stage.style.setProperty('--tag-b', Math.min(1, Math.max(0, (92 - x) / 14)).toFixed(2));
      const handle = $('[data-handle]', stage);
      if (handle) {
        const a = Math.round(x);
        handle.setAttribute('aria-valuenow', String(a));
        handle.setAttribute('aria-valuetext', (strings.compareValue || '__FIRST__ __A__%, __SECOND__ __B__%')
          .replace('__FIRST__', handle.dataset.labelA || '')
          .replace('__SECOND__', handle.dataset.labelB || '')
          .replace('__A__', String(a))
          .replace('__B__', String(100 - a)));
      }
    }
    settle(stage, value) {
      stage.classList.add('is-settling');
      this.setX(stage, value);
      window.clearTimeout(stage._settle);
      stage._settle = window.setTimeout(() => stage.classList.remove('is-settling'), 400);
    }
    bindStage(panel) {
      const stage = $('[data-stage]', panel);
      const handle = $('[data-handle]', panel);
      if (!stage || !handle) return;
      let drag = null;
      let rect = null;
      // The frame is measured once per gesture, not on every pointer move
      const toX = (clientX) => {
        const r = rect || stage.getBoundingClientRect();
        return ((clientX - r.left) / r.width) * 100;
      };
      stage.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || drag) return;
        this.interacted = true;
        rect = stage.getBoundingClientRect();
        const onHandle = !!event.target.closest('[data-handle]');
        if (event.pointerType === 'touch') {
          // On touch, wait for a horizontal intent (on the photo or on the handle): vertical swipes keep scrolling the page
          drag = { id: event.pointerId, x0: event.clientX, y0: event.clientY, locked: null, onHandle };
          return;
        }
        drag = { id: event.pointerId, locked: true };
        try { stage.setPointerCapture(event.pointerId); } catch (err) { /* pointer already gone */ }
        stage.classList.add('is-dragging');
        if (!onHandle) this.settle(stage, toX(event.clientX));
        event.preventDefault();
      });
      stage.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        if (drag.locked === null) {
          const dx = event.clientX - drag.x0;
          const dy = event.clientY - drag.y0;
          if (Math.hypot(dx, dy) < 8) return;
          drag.locked = Math.abs(dx) > Math.abs(dy);
          if (!drag.locked) { drag = null; return; }
          try { stage.setPointerCapture(event.pointerId); } catch (err) { /* pointer already gone */ }
          stage.classList.add('is-dragging');
        }
        stage.classList.remove('is-settling');
        this.setX(stage, toX(event.clientX));
      });
      const end = (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        // A tap on the photo moves the line there
        if (drag.locked === null && event.type === 'pointerup' && !drag.onHandle) this.settle(stage, toX(event.clientX));
        drag = null;
        rect = null;
        stage.classList.remove('is-dragging');
      };
      stage.addEventListener('pointerup', end);
      stage.addEventListener('pointercancel', end);
      handle.addEventListener('keydown', (event) => {
        const steps = { ArrowLeft: -2, ArrowDown: -2, ArrowRight: 2, ArrowUp: 2, PageDown: -10, PageUp: 10 };
        let next = null;
        if (event.key in steps) next = this.x + steps[event.key];
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = 100;
        if (next === null) return;
        event.preventDefault();
        this.interacted = true;
        this.settle(stage, next);
      });
    }
    // Once, when the figure is first seen: the line breathes left and right to show it moves
    hint() {
      const stage = $('[data-stage]', this);
      if (!stage || reducedMotion.matches || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        window.setTimeout(() => {
          const panel = this.panels[this.current];
          const visible = panel && $('[data-stage]', panel);
          if (!this.interacted && visible) this.sweep(visible, [50, 34, 60, 50], 1700);
        }, 300);
      }, { threshold: 0.6 });
      io.observe(stage);
    }
    sweep(stage, points, duration) {
      const start = performance.now();
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const segments = points.length - 1;
      const step = (now) => {
        if (this.interacted) return;
        const p = Math.min(1, (now - start) / duration);
        const pos = p * segments;
        const i = Math.min(segments - 1, Math.floor(pos));
        this.setX(stage, points[i] + (points[i + 1] - points[i]) * ease(pos - i));
        if (p < 1) window.requestAnimationFrame(step);
      };
      window.requestAnimationFrame(step);
    }
  }
  define('fit-compare', FitCompare);

  /* ---------- Set anatomy: pins, loupe and the champagne leader line ---------- */
  class SetAnatomy extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.panels = $$('[data-anatomy-panel]', this);
      this.index = -1;
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.showSet(Number(tab.dataset.tab)));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const n = this.tabs.length;
          const i = Number(tab.dataset.tab);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? n - 1 : (i + (event.key === 'ArrowRight' ? 1 : -1) + n) % n;
          this.tabs[next].focus();
          this.showSet(next);
        });
      });
      document.addEventListener('capsule:set', (event) => {
        if (event.detail.source === this) return;
        const index = this.panels.findIndex((p) => p.dataset.setKey === event.detail.key);
        if (index > -1) this.showSet(index, { broadcast: false, animate: false });
      });
      this.ro = new ResizeObserver(() => this.place(false));
      // The lens photo and the panel images load only when the section comes near the screen,
      // not on every visit to the page (the close-ups are the heaviest files on the home page)
      this.near = !('IntersectionObserver' in window);
      if (!this.near) {
        this.nearIo = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          this.near = true;
          this.nearIo.disconnect();
          this.wake();
        }, { rootMargin: '800px 0px' });
        this.nearIo.observe(this);
      }
      this.showSet(0, { broadcast: false, animate: false });
    }
    wake() {
      if (!this.panel) return;
      $$('img[loading="lazy"]', this.panel).forEach((img) => { img.loading = 'eager'; });
      this.place(false);
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.nearIo) this.nearIo.disconnect();
    }
    showSet(index, { broadcast = true, animate = true } = {}) {
      if (index === this.index || !this.panels[index]) return;
      this.index = index;
      this.panels.forEach((p, i) => { p.hidden = i !== index; });
      this.tabs.forEach((t, i) => {
        t.setAttribute('aria-selected', String(i === index));
        if (i === index) t.removeAttribute('tabindex'); else t.setAttribute('tabindex', '-1');
      });
      this.panel = this.panels[index];
      this.frame = $('.anatomy__frame', this.panel);
      this.loupe = $('[data-loupe]', this.panel);
      this.lens = $('[data-loupe-lens]', this.panel);
      this.lines = $('.anatomy__lines', this.panel);
      this.details = $$('[data-detail]', this.panel);
      this.pins = $$('[data-pin]', this.panel);
      this.details.forEach((btn) => {
        if (btn._bound) return;
        btn._bound = true;
        const go = () => this.activate(Number(btn.dataset.detail));
        btn.addEventListener('click', go);
        btn.addEventListener('focus', go);
        btn.addEventListener('mouseenter', () => { if (finePointer.matches) go(); });
      });
      this.pins.forEach((pin) => {
        if (pin._bound) return;
        pin._bound = true;
        pin.addEventListener('click', () => {
          const i = Number(pin.dataset.pin);
          this.activate(i);
          const item = this.details[i] && this.details[i].closest('.anatomy__item');
          if (item && !desktop.matches) item.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
        });
      });
      if (this.near) $$('img[loading="lazy"]', this.panel).forEach((img) => { img.loading = 'eager'; });
      if (animate && !reducedMotion.matches) {
        this.panel.classList.remove('is-entering');
        void this.panel.offsetWidth;
        this.panel.classList.add('is-entering');
        window.setTimeout(() => this.panel.classList.remove('is-entering'), 700);
      }
      if (this.ro && this.frame) { this.ro.disconnect(); this.ro.observe(this.frame); }
      this.observe();
      this.current = -1;
      if (this.loupe) this.loupe.classList.add('is-instant');
      this.activate(0, true);
      if (this.loupe) window.requestAnimationFrame(() => this.loupe.classList.remove('is-instant'));
      if (broadcast) document.dispatchEvent(new CustomEvent('capsule:set', { detail: { key: this.panel.dataset.setKey, source: this } }));
    }
    observe() {
      if (this.io) this.io.disconnect();
      if (!('IntersectionObserver' in window)) return;
      const items = this.details.map((d) => d.closest('.anatomy__item'));
      const list = $('[data-anatomy-list]', this.panel);
      // Desktop: the detail crossing the middle of the screen lights up. Mobile: the card in view.
      const options = desktop.matches ? { rootMargin: '-46% 0px -46% 0px', threshold: 0 } : { root: list, threshold: 0.6 };
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const i = items.indexOf(entry.target);
          if (i > -1) this.activate(i);
        });
      }, options);
      items.forEach((item) => this.io.observe(item));
    }
    activate(i, instant = false) {
      if (!this.details[i] || i === this.current) return;
      this.current = i;
      this.details.forEach((d, n) => d.setAttribute('aria-pressed', String(n === i)));
      this.pins.forEach((p, n) => p.classList.toggle('is-active', n === i));
      this.place(!instant);
    }
    place(animate = true) {
      const btn = this.details[this.current];
      if (!btn || !this.frame || !this.loupe) return;
      const w = this.frame.clientWidth;
      const h = this.frame.clientHeight;
      if (!w || !h) return;
      const px = (Number(btn.dataset.x) / 100) * w;
      const py = (Number(btn.dataset.y) / 100) * h;
      const size = this.loupe.offsetWidth || 208;
      const r = size / 2;
      // The loupe sits on the side with more room, a little above the pin
      const side = px < w / 2 ? 1 : -1;
      let cx = px + side * (r + Math.min(56, w * 0.08));
      let cy = py - r * 0.35;
      cx = Math.min(Math.max(cx, r + 8), w - r - 8);
      cy = Math.min(Math.max(cy, r + 8), h - r - 8);
      this.loupe.style.translate = `${(cx - r).toFixed(1)}px ${(cy - r).toFixed(1)}px`;
      this.loupe.style.setProperty('--origin', `${side > 0 ? 0 : 100}% 50%`);
      // Lens: the close-up when there is one, otherwise the garment magnified 2.2x around the pin
      if (!this.near) return;
      const closeup = btn.dataset.closeup;
      const source = closeup || this.panel.dataset.base || '';
      if (source && this.lens.dataset.src !== `${source}#${closeup ? 'c' : this.current}`) {
        this.lens.dataset.src = `${source}#${closeup ? 'c' : this.current}`;
        if (animate && !reducedMotion.matches) {
          this.lens.classList.remove('is-swapping');
          void this.lens.offsetWidth;
          this.lens.classList.add('is-swapping');
        }
      }
      if (closeup) {
        this.lens.style.backgroundImage = `url("${closeup}")`;
        this.lens.style.backgroundSize = 'cover';
        this.lens.style.backgroundPosition = 'center';
      } else if (this.panel.dataset.base) {
        const zoom = 2.2;
        this.lens.style.backgroundImage = `url("${this.panel.dataset.base}")`;
        this.lens.style.backgroundSize = `${w * zoom}px ${h * zoom}px`;
        this.lens.style.backgroundPosition = `${-(px * zoom - r)}px ${-(py * zoom - r)}px`;
      }
      this.loupe.classList.add('is-visible');
      // Leader line: a curve from the pin to the loupe's edge, drawn like the piping
      if (this.lines) {
        const dx = px - cx;
        const dy = py - cy;
        const dist = Math.hypot(dx, dy) || 1;
        const ex = cx + (dx / dist) * r;
        const ey = cy + (dy / dist) * r;
        const mx = (px + ex) / 2 - (dy / dist) * 18 * side;
        const my = (py + ey) / 2 + (dx / dist) * 18 * side;
        this.lines.setAttribute('viewBox', `0 0 ${w} ${h}`);
        const d = `M ${px.toFixed(1)} ${py.toFixed(1)} Q ${mx.toFixed(1)} ${my.toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)}`;
        $$('path', this.lines).forEach((path) => path.setAttribute('d', d));
        if (animate && !reducedMotion.matches) {
          this.lines.classList.remove('is-drawn');
          void this.lines.getBoundingClientRect();
          window.requestAnimationFrame(() => this.lines.classList.add('is-drawn'));
        } else {
          this.lines.classList.add('is-drawn');
        }
      }
    }
  }
  define('set-anatomy', SetAnatomy);

  /* ---------- Capsule index: the photo follows the line under the pointer or focus ---------- */
  class CapsuleIndex extends HTMLElement {
    connectedCallback() {
      this.rows = $$('[data-index-row]', this);
      this.shots = $$('[data-index-shot]', this);
      if (!this.shots.length) return;
      const show = (i) => this.shots.forEach((shot, n) => shot.classList.toggle('is-active', n === i));
      this.rows.forEach((row) => {
        const i = Number(row.dataset.indexRow);
        row.addEventListener('mouseenter', () => { if (finePointer.matches) show(i); });
        row.addEventListener('focus', () => show(i));
      });
      // Load the other photos before the visitor reaches the list, so a hover never shows a blank
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          $$('img[loading="lazy"]', this).forEach((img) => { img.loading = 'eager'; });
        }, { rootMargin: '600px 0px' });
        io.observe(this);
      }
    }
  }
  define('capsule-index', CapsuleIndex);

  /* ---------- Collection filters & sorting ---------- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.form = document.getElementById(this.dataset.form);
      const onChange = debounce(() => this.applyForm(), 350);
      if (this.form) {
        this.form.addEventListener('change', onChange);
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.applyForm().then(() => closeDialog(this.form.closest('dialog')));
        });
      }
      const sort = $('[data-sort]', this);
      if (sort) sort.addEventListener('change', () => {
        const hidden = this.form && $('[data-sort-hidden]', this.form);
        if (hidden) hidden.value = sort.value;
        if (this.form) this.applyForm();
        else window.location.search = `?sort_by=${encodeURIComponent(sort.value)}`;
      });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-link]');
        if (!link) return;
        event.preventDefault();
        this.load(new URL(link.href, window.location.origin)).then(() => closeDialog(link.closest('dialog')));
      });
      window.addEventListener('popstate', () => this.load(new URL(window.location.href), false));
    }
    applyForm() {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => { if (value !== '') params.append(key, value); });
      const sort = $('[data-sort]', this);
      if (sort) params.set('sort_by', sort.value);
      const url = new URL(window.location.pathname, window.location.origin);
      url.search = params.toString();
      return this.load(url);
    }
    async load(url, push = true) {
      const results = $('[data-facets-results]', this);
      if (results) results.classList.add('is-loading');
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      try {
        const res = await fetch(fetchUrl.toString());
        const doc = parseHTML(await res.text());
        const swap = (selector) => {
          const fresh = $(selector, doc);
          const current = $(selector, this);
          if (fresh && current) {
            current.innerHTML = fresh.innerHTML;
            if (fresh.hasAttribute('hidden')) current.setAttribute('hidden', ''); else current.removeAttribute('hidden');
          }
        };
        swap('[data-facets-results]');
        swap('[data-facets-count]');
        swap('[data-active-filters]');
        swap('[data-active-count]');
        swap('[data-results-label]');
        // Refresh filter values (counts, disabled states) but keep focus
        const body = $('[data-facets-body]', this);
        const freshBody = $('[data-facets-body]', doc);
        if (body && freshBody) {
          const focusedId = document.activeElement && document.activeElement.id;
          body.innerHTML = freshBody.innerHTML;
          if (focusedId) { const el = document.getElementById(focusedId); if (el) el.focus(); }
        }
        if (push) window.history.pushState({}, '', url.toString());
        announce($('[data-facets-count]', this) ? $('[data-facets-count]', this).textContent : '');
      } catch (e) {
        window.location.href = url.toString();
      } finally {
        if (results) results.classList.remove('is-loading');
      }
    }
  }
  define('facet-filters', FacetFilters);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enabled !== 'true') return;
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      this.controller = null;
      this.input.addEventListener('input', debounce(() => this.search(), 220));
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.input.value) { e.stopPropagation(); this.input.value = ''; this.clear(); }
      });
    }
    clear() {
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.clear(); return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const res = await fetch(`${routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(String(res.status));
        const doc = parseHTML(await res.text());
        const inner = $('.predictive__inner', doc);
        this.results.innerHTML = inner ? inner.outerHTML : '';
        this.input.setAttribute('aria-expanded', inner ? 'true' : 'false');
      } catch (e) {
        if (e.name !== 'AbortError') this.clear();
      }
    }
  }
  define('predictive-search', PredictiveSearch);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const load = () => fetch(this.dataset.url)
        .then((r) => r.text())
        .then((html) => {
          const doc = parseHTML(html);
          const fresh = $('product-recommendations', doc);
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          else { const section = this.closest('.shopify-section'); if (section) section.hidden = true; }
        })
        .catch(() => {});
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { io.disconnect(); load(); }
        }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else load();
    }
  }
  define('product-recommendations', ProductRecommendations);

  /* ---------- Withdrawal form (OUG 18/2026) ---------- */
  class WithdrawalForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      this.review = $('[data-withdrawal-step="2"]', this);
      this.done = $('[data-withdrawal-step="3"]', this);
      this.error = $('[data-withdrawal-error]', this);
      this.endpoint = (this.dataset.endpoint || '').replace(/\/$/, '');
      // One key per form, so a double submit records one withdrawal. The service expects a version 4 UUID.
      this.key = (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
        : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16));
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => {
        if (this.confirmed) return; // fallback submission to Shopify after confirmation
        event.preventDefault();
        if (!validateForm(this.form)) return;
        this.showReview();
      });
      $('[data-withdrawal-back]', this).addEventListener('click', () => this.showStep(1));
      $('[data-withdrawal-confirm]', this).addEventListener('click', (e) => this.confirm(e.currentTarget));
      this.restoreFallback();
    }
    // Fallback (Shopify contact form): after the page reloads with the success message, show what was sent and
    // when, instead of the same empty form again. The rows were kept for this one page load only.
    restoreFallback() {
      let saved = null;
      try { saved = JSON.parse(sessionStorage.getItem('cn-withdrawal') || 'null'); sessionStorage.removeItem('cn-withdrawal'); } catch (e) { /* storage off */ }
      const success = $('.form-message--success', this.form);
      if (!success) return;
      const message = success.textContent.trim();
      $('[data-withdrawal-done-text]', this).textContent = message;
      const when = saved && saved.at ? new Date(saved.at).toLocaleString(document.documentElement.lang || 'ro', { dateStyle: 'long', timeStyle: 'short' }) : '';
      $('[data-withdrawal-receipt]', this).textContent = when ? (this.dataset.recordedAt || '__DATE__').replace('__DATE__', when) : '';
      if (saved && saved.rows && saved.rows.length) this.fillRecap(saved.rows);
      const again = $('[data-withdrawal-again]', this);
      if (again) again.hidden = false;
      this.showStep(3);
    }
    summaryRows() {
      return $$('[data-field]', this.form).filter((el) => el.value.trim()).map((el) => {
        const label = this.form.querySelector(`label[for="${el.id}"]`);
        return [label ? label.childNodes[0].textContent.trim() : el.name, el.value.trim(), el.type === 'email'];
      });
    }
    fillList(dl, rows) {
      dl.innerHTML = '';
      rows.forEach(([term, value, isEmail]) => {
        const row = document.createElement('div');
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = term;
        dd.textContent = value;
        if (isEmail) dd.classList.add('is-email');
        row.append(dt, dd);
        dl.appendChild(row);
      });
    }
    fillRecap(rows) {
      const box = $('[data-withdrawal-recap]', this);
      if (!box) return;
      this.fillList($('[data-withdrawal-recap-list]', box), rows);
      box.hidden = false;
    }
    values() {
      const data = {};
      $$('[data-field]', this.form).forEach((el) => { data[el.dataset.field] = el.value.trim(); });
      return data;
    }
    showStep(step) {
      this.form.hidden = step !== 1;
      this.review.hidden = step !== 2;
      this.done.hidden = step !== 3;
      const target = step === 1 ? this.form : step === 2 ? this.review : this.done;
      target.focus ? target.focus({ preventScroll: false }) : null;
      target.scrollIntoView({ block: 'start', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
    showReview() {
      this.fillList($('[data-withdrawal-summary]', this), this.summaryRows());
      this.showStep(2);
    }
    async confirm(button) {
      this.error.hidden = true;
      if (!this.endpoint) {
        // No withdrawal service configured: send through the Shopify contact form
        this.confirmed = true;
        try { sessionStorage.setItem('cn-withdrawal', JSON.stringify({ rows: this.summaryRows(), at: Date.now() })); } catch (e) { /* storage off */ }
        this.form.hidden = false;
        this.form.requestSubmit();
        return;
      }
      button.classList.add('is-loading');
      const payload = {
        ...this.values(),
        submissionKey: this.key,
        website: ($('#WithdrawalWebsite', this) || {}).value || '',
        confirmed: true,
      };
      try {
        const res = await fetch(`${this.endpoint}/withdrawals`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || strings.cartError);
        const when = data.createdAt ? new Date(data.createdAt).toLocaleString(document.documentElement.lang || 'ro') : '';
        $('[data-withdrawal-receipt]', this).textContent = `${data.id || ''} · ${when}`;
        this.fillRecap(this.summaryRows());
        this.showStep(3);
      } catch (error) {
        this.showStep(1);
        this.error.hidden = false;
        this.error.textContent = error.message;
      } finally {
        button.classList.remove('is-loading');
      }
    }
  }
  define('withdrawal-form', WithdrawalForm);

  /* ---------- Page transitions: morph the clicked product image ---------- */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-card-link]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || reducedMotion.matches) return;
    $$('[style*="view-transition-name"]').forEach((el) => { if (el.closest('[data-product-card]')) el.style.viewTransitionName = ''; });
    const card = link.closest('[data-product-card]');
    const img = card && $('.card__img--first', card);
    if (img) img.style.viewTransitionName = 'product-media';
  });
  window.addEventListener('pageshow', () => {
    $$('[data-product-card] .card__img--first').forEach((img) => { img.style.viewTransitionName = ''; });
  });
})();
